import { useState, useEffect } from 'react';
import { useApp } from '@context/AppContext';
import { useTelephone } from './useTelephone';
import GameLobby from '../shared/GameLobby';
import { Clock, MessageCircle } from 'lucide-react';

const DRAW_TIME_OPTIONS = [
  { label: '60s', value: 60 },
  { label: '90s', value: 90 },
  { label: '2 min', value: 120 },
];

const GUESS_TIME_OPTIONS = [
  { label: '30s', value: 30 },
  { label: '45s', value: 45 },
  { label: '60s', value: 60 },
];

export default function TelephoneLobby() {
  const { state } = useApp();
  const { game, user, room } = state;
  const { initTelephone, startTelephone, endGame } = useTelephone();

  const [drawDuration, setDrawDuration] = useState(game.drawDuration || 60);
  const [guessDuration, setGuessDuration] = useState(game.guessDuration || 30);

  const memberCount = Object.keys(room.members || {}).length;

  // Host: initialise chains when lobby opens (or when settings change)
  useEffect(() => {
    if (!user.isHost) return;
    if (!game.chains) {
      initTelephone({ drawDuration, guessDuration });
    }
  }, [user.isHost]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSettingsChange = (newDraw, newGuess) => {
    setDrawDuration(newDraw);
    setGuessDuration(newGuess);
    initTelephone({ drawDuration: newDraw, guessDuration: newGuess });
  };

  const handleStart = () => {
    startTelephone();
  };

  return (
    <GameLobby
      title="Broken Telephone"
      emoji="📞"
      onStart={handleStart}
      onCancel={endGame}
      startLabel="Start Game!"
      startDisabled={memberCount < 3 || memberCount > 6}
    >
      {/* Historical blurb */}
      <div className="bg-sky-50 border border-sky-100 rounded-xl p-4">
        <div className="flex items-start gap-2.5">
          <MessageCircle className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-charcoal font-display leading-relaxed">
              A mashup of Telephone and drawing — each step reinterprets the last. One person draws it,
              the next guesses what it is, the next draws that guess. The progressive drift is the whole point.
            </p>
            <p className="text-xs text-slate-400 font-display mt-1.5">
              Commercially known as Telestrations since 2009.
            </p>
          </div>
        </div>
      </div>

      {/* Round info */}
      <div className="bg-amber/5 border border-amber/20 rounded-xl px-4 py-3">
        <p className="text-sm font-semibold text-charcoal font-display">
          {memberCount} players = {memberCount} rounds per chain
        </p>
        <p className="text-xs text-slate-400 font-display mt-0.5">
          Each player starts a chain. Everyone contributes to every chain.
        </p>
      </div>

      {memberCount < 3 && (
        <p className="text-sm text-red-500 font-display text-center">
          Need at least 3 players for Broken Telephone.
        </p>
      )}
      {memberCount > 6 && (
        <p className="text-sm text-amber font-display text-center">
          Broken Telephone works best with 3–6 players. With more, it gets too slow.
        </p>
      )}

      {/* Settings (host only) */}
      {user.isHost && (
        <div className="space-y-3">
          {/* Drawing time */}
          <div>
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Drawing Time
            </p>
            <div className="flex gap-2">
              {DRAW_TIME_OPTIONS.map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => handleSettingsChange(value, guessDuration)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-display transition-all ${
                    drawDuration === value
                      ? 'bg-amber text-white font-semibold'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Guessing time */}
          <div>
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Guessing Time
            </p>
            <div className="flex gap-2">
              {GUESS_TIME_OPTIONS.map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => handleSettingsChange(drawDuration, value)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-display transition-all ${
                    guessDuration === value
                      ? 'bg-amber text-white font-semibold'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Non-host waiting message */}
      {!user.isHost && (
        <p className="text-sm text-slate-400 font-display text-center">
          Waiting for host to start the game...
        </p>
      )}
    </GameLobby>
  );
}
